import { CardBody, CardHeader, CardFooter, Card, Button } from 'reactstrap';
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import SaveButton from '../common/controls/saveButton';
import Icon from '../common/containers/icon';
import EditorComponent from '../editor/editorComponent';
import { checkPermission, makeResource } from '../../../behaviors/src/lib/permissions';
const FormAnnotationEditorSidebar = _ref => {
  var _formActive$formType2;
  let containerMaxHeight = _ref.containerMaxHeight,
    _ref$displayType = _ref.displayType,
    displayType = _ref$displayType === void 0 ? null : _ref$displayType,
    active = _ref.annotation.active,
    _ref$form = _ref.form,
    formActive = _ref$form.active,
    pendingChanges = _ref$form.pendingChanges,
    loading = _ref$form.loading,
    _ref$formActions = _ref.formActions,
    change = _ref$formActions.change,
    serverSave = _ref$formActions.serverSave,
    serverShow = _ref$formActions.serverShow,
    serverDelete = _ref$formActions.serverDelete,
    _ref$login = _ref.login,
    login = _ref$login === void 0 ? {} : _ref$login;
  const prevActiveId = useRef();

  // Add effect to load form data when annotation.active changes
  useEffect(() => {
    // Only call serverShow if we have a new annotation ID and it's different from the previous one
    if (active && active.id && active.id !== prevActiveId.current) {
      // Check if we already have form data for this annotation to avoid unnecessary server calls
      // formActive should have both id and content/format to be considered valid
      const hasFormData = formActive && formActive.id === active.id && formActive.content && formActive.format;

      // Only call serverShow if we don't already have the form data
      if (!hasFormData) {
        serverShow(active.id);
      }
      prevActiveId.current = active.id;
    }
  }, [active, serverShow, formActive]);

  // Reset prevActiveId when annotation is cleared
  useEffect(() => {
    if (!active || !active.id) {
      prevActiveId.current = undefined;
    }
  }, [active]);

  // SaveButton expects 'pending', 'inProgress', or 'disabled'
  const saveState = pendingChanges ? 'pending' : 'disabled';
  const selectionMessage = displayType ? /*#__PURE__*/React.createElement("h4", {
    className: "t-faes-edit-header"
  }, "Select ", displayType.charAt(0) === 'i' ? 'an' : 'a', " ", displayType, " annotation") : /*#__PURE__*/React.createElement("h4", {
    className: "t-faes-header"
  }, "Select an annotation type to edit");
  const content = formActive === null || formActive === void 0 ? void 0 : formActive.content;
  const format = formActive === null || formActive === void 0 ? void 0 : formActive.format;

  // Preprocess format to remove section label (similar to how formEditor handles it)
  // This can be removed once sections are handled via tabs
  const processedFormat = useMemo(() => {
    if (!format) return format;
    const modifiedFormat = JSON.parse(JSON.stringify(format));

    // Helper function to find and remove the first section label
    const removeFirstSectionLabel = formatObj => {
      if (formatObj.type === 'section') {
        // eslint-disable-next-line no-param-reassign
        delete formatObj.label;
        return true; // Found and removed
      }
      if (formatObj.children && Array.isArray(formatObj.children)) {
        return formatObj.children.some(child => removeFirstSectionLabel(child));
      }
      return false; // Not found
    };
    removeFirstSectionLabel(modifiedFormat);
    return modifiedFormat;
  }, [format]);

  // Check if user has allow_form_delete permission for the current form type
  const user = login === null || login === void 0 ? void 0 : login.user;
  // Determine the form type identifier from formActive or active
  const canDelete = useMemo(() => {
    var _formActive$formType;
    const formTypeIdentifier = formActive === null || formActive === void 0 || (_formActive$formType = formActive.formType) === null || _formActive$formType === void 0 ? void 0 : _formActive$formType.identifier;
    const userPermissions = (user === null || user === void 0 ? void 0 : user.permissions) || {};
    const formResource = makeResource('form', formTypeIdentifier);
    return checkPermission(formResource, 'delete', userPermissions, user) === true;
  }, [formActive === null || formActive === void 0 || (_formActive$formType2 = formActive.formType) === null || _formActive$formType2 === void 0 ? void 0 : _formActive$formType2.identifier, user]);
  return /*#__PURE__*/React.createElement(Card, {
    style: {
      width: '100%',
      height: containerMaxHeight,
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement(CardHeader, {
    id: "detail-card-header",
    style: {
      paddingBottom: 20,
      paddingLeft: 10,
      paddingRight: 10
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      float: 'right'
    }
  }, /*#__PURE__*/React.createElement(SaveButton, {
    onSave: () => serverSave(format === null || format === void 0 ? void 0 : format.version),
    saveState: saveState,
    className: "t-faes-save",
    size: "sm"
  }))), /*#__PURE__*/React.createElement(CardBody, {
    style: {
      // Use flex-grow to fill remaining space after header and footer
      flex: 1,
      overflow: 'auto'
    },
    "data-testid": "t-faes-card-body"
  }, format && content ? /*#__PURE__*/React.createElement(EditorComponent, {
    reduxEntity: "form",
    format: processedFormat,
    content: content,
    path: "/",
    onChange: _ref2 => {
      let path = _ref2.path,
        value = _ref2.value,
        changeModifiers = _ref2.changeModifiers;
      return change(content.id,
      // "change" dedupes any double slashes, so safe to err on more here
      path.startsWith('/content') ? path : ['/content', path].join('/'), value, changeModifiers);
    },
    operationComplete: () => {},
    operationPending: () => {}
  }) : /*#__PURE__*/React.createElement("div", {
    style: {
      height: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start'
    }
  }, loading ? /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "loader",
    style: {
      margin: '20px auto'
    }
  }), /*#__PURE__*/React.createElement("p", null, "Loading annotation data...")) : selectionMessage)), /*#__PURE__*/React.createElement(CardFooter, {
    style: {
      paddingLeft: 10,
      paddingRight: 10,
      height: 59,
      display: 'flex',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    className: "float-left t-faes-remove",
    onClick: () => {
      if (active && active.id) {
        serverDelete(active.id);
      }
    },
    disabled: !active || !active.id || !canDelete
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "trash"
  }), " Remove ", displayType)));
};
FormAnnotationEditorSidebar.propTypes = {
  containerMaxHeight: PropTypes.number.isRequired,
  displayType: PropTypes.string,
  annotation: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  form: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string,
      content: PropTypes.shape({}),
      format: PropTypes.shape({}),
      formType: PropTypes.shape({
        identifier: PropTypes.string
      })
    }),
    pendingChanges: PropTypes.bool,
    loading: PropTypes.bool
  }).isRequired,
  formActions: PropTypes.shape({
    change: PropTypes.func.isRequired,
    serverSave: PropTypes.func.isRequired,
    serverShow: PropTypes.func.isRequired,
    serverDelete: PropTypes.func.isRequired
  }).isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      permissions: PropTypes.shape({
        allow_form_delete: PropTypes.bool
      })
    })
  })
};
export { FormAnnotationEditorSidebar as UnComposedFormAnnotationEditorSidebar };
export default entityComposition(FormAnnotationEditorSidebar, ['annotation', 'form', 'login']);